/* eslint-disable no-param-reassign */
import { produce } from 'immer';
import { indexBy, prop } from 'ramda';

import * as types from 'constants/ActionTypes';

/**
 * @typedef {{
 *   data: {
 *     all: {
 *        items: Record<string, import("modules/offers/types").Offer>,
 *        page: import("modules/global/generic.types").Page,
 *     }
 *   },
 *   selectedOfferId: string,
 *   isCreatingIntervention: boolean,
 *   isPrintingSpecs: boolean,
 *   apiError: string,
 *   isFetching: boolean,
 *   modal: {
 *     settings: {
 *        visible: boolean,
 *        customerCreationVisible: boolean,
 *        data: import("modules/offers/types").OfferModalValue,
 *     },
 *     consent: {
 *        visible: boolean,
 *        alreadyConsent: boolean,
 *     }
 *   },
 * }} OfferReducer
 */

const initialState = {
  data: {
    all: { items: {}, links: {}, page: {} },
  },
  apiError: null,
  isFetching: false,
  selectedOfferId: '',
  modal: {
    settings: {
      visible: false,
      customerCreationVisible: false,
      data: {},
    },
    consent: {
      visible: false,
      alreadyConsent: false,
    },
  },
  isPrintingSpecs: false,
  isCreatingIntervention: false,
};

const reducer = produce(
  /** @param {OfferReducer} draft  */
  (draft, action) => {
    switch (action.type) {
      case types.FETCH_OFFER_REQUEST:
      case types.FETCH_OFFERS_REQUEST:
        draft.isFetching = true;
        draft.apiError = null;
        draft.modal.consent.alreadyConsent = false;
        break;

      case types.FETCH_OFFER_SUCCESS:
        draft.isFetching = false;
        draft.data.all.items[action.offer.id] = action.offer;
        draft.selectedOfferId = action.offer.id;
        break;

      case types.FETCH_OFFERS_SUCCESS:
        draft.isFetching = false;
        draft.data.all.items = indexBy(prop('id'))(action.offers);
        draft.data.all.page = action.page;
        draft.data.all.links = indexBy(prop('rel'))(action.links);
        break;

      case types.COPY_OFFER_SUCCESS:
        draft.data.all.items[action.offer.id] = action.offer;
        draft.selectedOfferId = action.offer.id;
        break;

      case types.FETCH_OFFER_FAILURE:
      case types.FETCH_OFFERS_FAILURE:
        draft.isFetching = false;
        draft.apiError = action.error;
        break;

      case types.SELECT_OFFER:
        draft.selectedOfferId = action.offerId;
        break;

      case types.CLEAN_OFFERS:
        draft.data = {
          all: { items: {}, links: {}, page: {} },
        };
        break;

      case types.SAVE_OFFER_SUCCESS:
        draft.data.all.items[action.offer.id] = action.offer;
        draft.selectedOfferId = action.offer.id;
        break;

      case types.RESET_NEW_OFFER:
        draft.apiError = null;
        draft.modal.consent.alreadyConsent = false;
        break;

      case types.DISPLAY_SETTINGS_MODAL:
        draft.modal.settings.visible = true;
        break;

      case types.HIDE_SETTINGS_MODAL:
        draft.modal.settings.visible = false;
        break;

      case types.DISPLAY_CUSTOMER_CREATION:
        draft.modal.settings.customerCreationVisible = true;
        break;

      case types.HIDE_CUSTOMER_CREATION:
        draft.modal.settings.customerCreationVisible = false;
        break;

      case types.CREATE_CUSTOMER_SUCCESS:
        draft.modal.settings.visible = false;
        draft.modal.settings.customerCreationVisible = false;
        break;

      case types.SET_OFFER_MODAL_VALUE:
        draft.modal.settings.data = action.payload.data;
        break;

      case types.FETCH_OFFER_SPECS_PDF_REQUEST:
        draft.isPrintingSpecs = true;
        break;

      case types.FETCH_OFFER_SPECS_PDF_SUCCESS:
      case types.FETCH_OFFER_SPECS_PDF_FAILURE:
        draft.isPrintingSpecs = false;
        break;

      case types.DISPLAY_CONSENT_MODAL:
        draft.modal.consent.visible = true;
        break;

      case types.HIDE_CONSENT_MODAL:
        draft.modal.consent.visible = false;
        break;

      case types.CONSENT_OFFER:
        draft.modal.consent.alreadyConsent = true;
        break;

      case types.POST_INTERVENTION_OFFER:
        draft.isCreatingIntervention = true;
        break;

      case types.POST_INTERVENTION_OFFER_SUCCESS:
      case types.POST_INTERVENTION_OFFER_FAILURE:
        draft.isCreatingIntervention = false;
        break;

      default:
    }
  },
  initialState
);
export default reducer;
